import { Box, Divider, Stack, SxProps } from '@mui/material';
import { Button, Checkbox, colors, Typography } from '@sweep-io/sweep-design';
import { SfAppElement } from './AppsConnectionDialog';
import { useSweepApi } from '../../../../apis/sweep';
import { useAiAssistant } from '../useAiAssistant';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { useState } from 'react';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useRunOnce } from '../../../common/useRunOnce';
import { selectApps } from '../../../../reducers/aiSupportReducer';
import { telemetry } from '../../../../telemetry';
import { ImageWithFallback } from './ImageWithFallback';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';

interface AppsConnectionProps {
  closeDialog: (event: any, isRefresh?: boolean) => void;
  containerSx?: SxProps;
}

export const AppsConnection = ({ closeDialog, containerSx }: AppsConnectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const sweepApi = useSweepApi();
  const { fetchApps } = useAiAssistant();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const listOfApps: SfAppElement[] | undefined = useSelector(selectApps(crmOrgId ?? ''));
  const [initiallyOnApps, setInitiallyOnApps] = useState<SfAppElement[]>([]);
  const [selectedApps, setSelectedApps] = useState<SfAppElement[]>([]);
  const { addNotification } = useSweepNotifications();
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  useRunOnce(async () => {
    const response = await fetchApps(crmOrgId ?? '');
    const supportedApps = response?.filter((el: SfAppElement) => el.hasSweepSupport);
    setSelectedApps(supportedApps);
    setInitiallyOnApps(supportedApps);
    setIsLoading(false);
  });

  const addToSalesforce = async (e: any) => {
    setIsButtonLoading(true);
    const addedAppLabelList: string[] = [];
    const removedAppLabelList: string[] = [];
    const utilityBarToAppDto = selectedApps.map((el) => {
      if (!initiallyOnApps.includes(el)) {
        addedAppLabelList.push(el.label);
      }
      return {
        appDeveloperName: el.developerName,
        hasSweepSupport: el.hasSweepSupport,
      };
    });
    const removedApps = listOfApps
      ?.map((el) => {
        if (initiallyOnApps.includes(el) && !selectedApps.includes(el)) {
          removedAppLabelList.push(el.label);
          return {
            appDeveloperName: el.developerName,
            hasSweepSupport: el.hasSweepSupport,
          };
        }
      })
      .filter((i) => i);
    try {
      const response = await sweepApi.post(`/utility-bar`, {
        toggleOn: utilityBarToAppDto,
        toggleOff: removedApps,
      });
      const isError = response?.data?.on?.some(
        (el: { status: string }) => el?.status === 'rejected',
      );
      const addedStr = 'added to ' + addedAppLabelList.join(', ');
      const removedStr = 'removed from ' + removedAppLabelList.join(', ');
      const isAdded = addedAppLabelList.length > 0;
      const isRemoved = removedAppLabelList.length > 0;
      const separator = isAdded && isRemoved ? ', ' : '';

      if (!isError) {
        await fetchApps(crmOrgId ?? '');
      }

      const _msg = `AI assistant was successfully ${isAdded ? addedStr : ''}${separator}${isRemoved ? removedStr : ''}`;
      addNotification({
        message: isError ? 'Unable to add AI assistant to Apps' : _msg,
        variant: isError ? SweepNotificationVariant.Error : SweepNotificationVariant.Success,
      });
      closeDialog(e, true);
    } catch (error) {
      telemetry.captureError(error);
      closeDialog(e);
    }
  };

  const listItemElement = (elem: SfAppElement) => {
    const onClickCheck = (isChecked: boolean) => {
      isChecked
        ? setSelectedApps([...selectedApps, elem])
        : setSelectedApps(selectedApps.filter((el) => el !== elem));
    };
    const onClickRow = () => {
      const isChecked = !!selectedApps?.find((el) => el.developerName === elem.developerName);
      isChecked
        ? setSelectedApps(selectedApps.filter((el) => el !== elem))
        : setSelectedApps([...selectedApps, elem]);
    };

    const initialsFromString = elem?.label?.match(/\b(\w)/g)?.join('');
    const _fallback = (
      <Box
        sx={{
          width: '24px',
          height: '24px',
          background: 'darkcyan',
          color: 'white',
          fontSize: '10px',
          textAlign: 'center',
          lineHeight: '24px',
          borderRadius: '2px',
          overflow: 'hidden',
          fontWeight: 500,
        }}
      >
        {initialsFromString}
      </Box>
    );

    return (
      <Box
        onClick={onClickRow}
        key={elem.developerName}
        sx={{
          display: 'flex',
          border: '1px solid',
          borderColor: colors.grey[300],
          borderRadius: '4px',
          p: '12px 16px',
          mb: '12px',
          gap: '12px',
          alignItems: 'center',
          '&:hover': {
            borderColor: colors.blue[500],
          },
        }}
      >
        <ImageWithFallback url={elem?.logoUrl} fallback={_fallback} />
        <Typography variant="body">{elem.label}</Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <Checkbox
            checked={selectedApps.includes(elem)}
            onChange={(value) => {
              const isChecked = value.target.checked;
              onClickCheck(isChecked);
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Stack height="100%" overflow="auto">
      {isLoading && (
        <Box m={2}>
          <CenteredCircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          <Box sx={{ margin: '0 56px 12px 0', pl: 2, pr: 2 }}>
            <Typography variant="body" color={colors.grey[700]}>
              The AI assistant will be added to the Salesforce utility bar in any Lightning app you
              choose below:
            </Typography>
          </Box>
          <Box
            sx={{
              pl: 2,
              maxHeight: '408px',
              overflow: 'hidden',
              mb: '24px',
              overflowY: 'auto',
              pr: 2,
              ...(containerSx ?? {}),
            }}
          >
            {listOfApps?.map(listItemElement)}
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'row-reverse', p: 2 }}>
            <Button
              loading={isButtonLoading}
              onClick={addToSalesforce}
              size="small"
              variant="primary"
              disabled={initiallyOnApps?.length === 0 && selectedApps?.length === 0}
            >
              {initiallyOnApps?.length === 0 ? 'Add to Salesforce' : 'Update in Salesforce'}
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};
